import * as yup from "yup";
import { QuoteType } from "../../types/QuoteType";
import { YesNo } from "../enums";
import {
  BorrowerInfo,
  DealTerms,
  FixAndFlipOverrideOptions,
  LoanSummary,
  LoanTerm,
  ShortTermUnderwriterFields,
} from "./short-term.model";
export class FixAndFlipForm
  implements
    ShortTermUnderwriterFields,
    FixAndFlipOverrideOptions,
    LoanSummary,
    LoanTerm,
    DealTerms,
    BorrowerInfo
{
  afterCompletedVal: number = 0;
  appraisalFee: number = 0;
  borrowingEntity: string = "";
  borrowerIsReferral: YesNo = YesNo.No;
  brokerFee: number = 0;
  brokerProcessingFee: number = 0;
  buildsCompletedInThreeYears: number = 0;
  cashOut: YesNo = YesNo.Yes;
  channel: string = "";
  city: string = "";
  combinedLiquidity: number = 0;
  enabled: boolean = false;
  existingDebtStatus: string = "";
  fnfCompletedInThreeYears: number = 0;
  guaranty: string = "Full Recourse";
  heavyRehab: YesNo = YesNo.No;
  lesserOfLotOrPurchase: number = 0;
  loanProduct: string = "";
  loanPurpose: string = "";
  loanTerm: string = "";
  overrideMaster: boolean = false;
  twoFourUnit: YesNo = YesNo.No;
  primaryGuarantor: string = "";
  projectInspectionFee: number = 0;
  propertyAddress: string = "";
  propertyType: string = "";
  listedPropertyStatus: string = "";
  qualifyingFICO: number = 0;
  quoteName: string = "New Short Term Finance Quote";
  quoteType: QuoteType = "STF";
  referralFee: YesNo = YesNo.No;
  internalRefinance: YesNo = YesNo.No;
  exitFeeCheck: YesNo = YesNo.No;
  refinance: YesNo = YesNo.No;
  rehabConBudget: number = 0;
  totalPurchasePrice: number = 0;
  completedTransactions: number = 0;
  secondaryGuarantor: string = "";
  state: string = "";
  targetCloseDate: string = "";
  tier: string = "1";
  timeCloseToPayoff: number = 0;
  usCitizen: YesNo = YesNo.Yes;
  zip: string = "";
  originalPurchaseDate: string = "";
  payoff: number = 0;
  username?: string = "";
  borrowerTier: string = "1";
  altTier?: YesNo = YesNo.No;
  buyDownPercentage: number = 0;
  buyUpPercentage: number = 0;
  blendedLTCOverride?: number | string = "";
  purchaseLTCOverride?: number | string = "";
  arvOverride?: number | string = "";
  exceptionType?: string = "";
  rateReductionOverride?: number = 0;
  finalRateOverride?: number | string = "";
  finalOrigFeeOverride?: number | string = "";
  autoRate?: number | string = 0;
  autoFee?: number | string = 0;
  commissionReduction?: number | string = 0;
  isZeroOrigFee: YesNo = YesNo.No;
  numOfProperties: number = 1;
  legalFee: number | string = "";
  interestRateAddOn?: number | string = 0;
  originationPointsAddOn?: number | string = 0;
  applicationFee?: number | string = "";
  documentFee?: number | string = "";
  processingFee?: number | string = 0;
  lenderFinanceAppraisalFee?: number | string = "";
  rpolt6?: boolean;
  rpogt6?: boolean;
  purchase?: boolean;
  adjustedARV?: number | string = "";
  adjustedPurchaseLTC?: number | string = "";
  adjustedBlendedLTC?: number | string = "";
  submittedDate?: string = "";
  dealBudgetPricing?: string = "";
  deferredOrigPercent?: number = 0;
  iREscrowOverrideInt?: number;
  iREscrowOverrideMoneyAmt?: number;
  processingFeeOverride?: number;
  achExempt?: string = "Non-exempt";
  achRequired?: string = YesNo.Yes;
  completedExits?: number = 0;
  wholesalePartner?: string = "";
  highRisk?: YesNo = YesNo.No;
  renovationCostsAfterPurchase?: string = "";
  monthsOfInterestReserves?: number;
  originationFeeAdjustment?: number = 1.25;
  maxInterestReserve?: number;
  documentPrepFee?: string = "";

  sfaccountid?: string = "";
  sfdealid?: string = "";
  campaignId?: string = "";
  campaignCode?: string = "";
  campaignExpirationDate?: string = "";
  campaignIncentive?: string = "";
  campaignName?: string = "";

  constructor(entity: any) {
    if (!entity) return;

    this.afterCompletedVal = entity.dealTerms.afterCompletedVal;
    this.appraisalFee = entity.loanTerm.appraisalFee;
    this.borrowingEntity = entity.borrowerInfo.borrowingEntity;
    this.borrowerIsReferral = entity.borrowerInfo.borrowerIsReferral ?? "No";
    this.brokerFee = entity.loanTerm.brokerFee;
    this.brokerProcessingFee = entity.loanTerm.brokerProcessingFee;
    this.buildsCompletedInThreeYears =
      entity.borrowerInfo.buildsCompletedInThreeYears;
    this.cashOut = entity.dealTerms.cashOut;
    this.channel = entity.loanTerm.channel ?? "Standard Retail";
    this.city = entity.loanSummary.city;
    this.combinedLiquidity = entity.borrowerInfo.combinedLiquidity;
    this.existingDebtStatus = entity.dealTerms.existingDebtStatus;
    this.fnfCompletedInThreeYears =
      entity.borrowerInfo.fnfCompletedInThreeYears;
    this.guaranty = entity.loanTerm.guaranty;
    this.monthsOfInterestReserves = entity.loanTerm.monthsOfInterestReserves;
    this.originationFeeAdjustment = (()=> {
      if(entity.originationFeeAdjustment) return entity.originationFeeAdjustment  
      if(entity.loanSummary.loanProduct === "FixNFlip") return 1.25
      return 1.5
      })();
    this.heavyRehab = entity.dealTerms.heavyRehab;
    this.lesserOfLotOrPurchase = entity.dealTerms.lesserOfLotOrPurchase;
    this.loanProduct = entity.loanSummary.loanProduct;
    this.loanPurpose = entity.loanSummary.loanPurpose;
    this.loanTerm = entity.loanTerm.loanTerm;
    this.overrideMaster = entity.overrideMaster;
    this.twoFourUnit = entity.twoFourUnit;
    this.primaryGuarantor = entity.borrowerInfo.primaryGuarantor;
    this.projectInspectionFee = entity.loanTerm?.projectInspectionFee ?? 0;
    this.propertyAddress = entity.loanSummary.propertyAddress;
    this.propertyType = entity.loanSummary.propertyType;
    this.listedPropertyStatus = entity.loanSummary.listedPropertyStatus;
    this.qualifyingFICO = entity.borrowerInfo.qualifyingFICO;
    this.quoteName = entity.quoteName;
    this.quoteType = entity.quoteType;
    this.referralFee = entity.loanTerm.referralFee;
    this.internalRefinance = entity.loanTerm.internalRefinance ?? "No";
    this.exitFeeCheck = entity.loanTerm.exitFeeCheck ?? "No";
    this.refinance = entity.dealTerms.refinance;
    this.rehabConBudget = entity.dealTerms.rehabConBudget;
    this.renovationCostsAfterPurchase =
      entity.dealTerms.renovationCostsAfterPurchase;
    this.totalPurchasePrice = entity.dealTerms.totalPurchasePrice;
    this.completedTransactions =
      entity.borrowerInfo?.completedTransactions ??
      entity.borrowerInfo.rentalUnitsOwned;
    this.secondaryGuarantor = entity.borrowerInfo.secondaryGuarantor;
    this.state = entity.loanSummary.state;
    this.targetCloseDate = entity.dealTerms.targetCloseDate;
    this.timeCloseToPayoff = entity.dealTerms.timeCloseToPayoff;
    this.usCitizen = entity.borrowerInfo.usCitizen;
    this.zip = entity.loanSummary.zip;
    this.originalPurchaseDate = entity.dealTerms.originalPurchaseDate;
    this.payoff = entity.dealTerms.payoff;
    this.username = entity?.username ?? "";
    this.borrowerTier = entity?.borrowerInfo.borrowerTier ?? "1";
    this.altTier = entity?.borrowerInfo.altTier ?? "No";
    this.buyUpPercentage = entity.dealTerms.buyUpPercentage ?? 0;
    this.buyDownPercentage = entity.dealTerms.buyDownPercentage ?? 0;
    this.sfaccountid = entity?.sfaccountid ?? "";
    this.sfdealid = entity?.sfdealid ?? "";
    this.campaignId = entity?.campaignId ?? "";
    this.campaignCode = entity?.campaignCode ?? "";
    this.campaignExpirationDate = entity?.campaignExpirationDate ?? "";
    this.campaignIncentive = entity?.campaignIncentive ?? "";
    this.campaignName = entity?.campaignName ?? "";
    this.blendedLTCOverride =
      entity.exceptionPricingOverride.blendedLTCOverride ?? "";
    this.purchaseLTCOverride =
      entity.exceptionPricingOverride.purchaseLTCOverride ?? "";
    this.arvOverride = entity.exceptionPricingOverride.arvOverride ?? "";
    this.commissionReduction =
      entity.exceptionPricingOverride.commissionReduction ?? 0;
    this.autoFee = entity?.exceptionPricingOverride.autoFee ?? 0;
    this.autoRate = entity?.exceptionPricingOverride.autoRate ?? 0;
    this.finalRateOverride =
      entity.exceptionPricingOverride.finalRateOverride ?? "";
    this.finalOrigFeeOverride =
      entity.exceptionPricingOverride.finalOrigFeeOverride ?? "";
    this.isZeroOrigFee = entity.dealTerms.isZeroOrigFee ?? "No";
    this.rateReductionOverride =
      entity?.exceptionPricingOverride.rateReductionOverride ??
      this.rateReductionOverride;
    this.exceptionType =
      entity?.exceptionPricingOverride.exceptionType ?? this.exceptionType;
    this.achExempt = entity?.exceptionPricingOverride.achExempt ?? "Non-exempt";
    this.achRequired =
      entity?.exceptionPricingOverride.achRequired ?? YesNo.Yes;
    this.numOfProperties =
      entity.dealTerms?.numOfProperties ?? this.numOfProperties;
    this.legalFee = entity?.underwriterFields?.legalFee ?? this.legalFee;
    this.interestRateAddOn =
      entity?.lenderFinance?.interestRateAddOn !== ""
        ? entity?.lenderFinance?.interestRateAddOn
        : 0;
    this.originationPointsAddOn =
      entity?.lenderFinance?.originationPointsAddOn !== ""
        ? entity?.lenderFinance?.originationPointsAddOn
        : 0;
    this.applicationFee =
      entity?.lenderFinance?.applicationFee ?? this.applicationFee;
    this.documentFee = entity?.lenderFinance?.documentFee ?? this.documentFee;
    this.lenderFinanceAppraisalFee =
      entity?.lenderFinance?.lenderFinanceAppraisalFee ??
      this.lenderFinanceAppraisalFee;
    this.processingFee =
      entity?.lenderFinance?.processingFee !== ""
        ? entity?.lenderFinance?.processingFee
        : 0;
    this.rpolt6 = entity?.lenderFinance?.rpolt6 ?? this.rpolt6;
    this.rpogt6 = entity?.lenderFinance?.rpogt6 ?? this.rpogt6;
    this.purchase = entity?.lenderFinance?.purchase ?? this.purchase;
    this.adjustedBlendedLTC =
      entity?.leverage?.adjustedBlendedLTC ?? this.adjustedBlendedLTC;
    this.adjustedPurchaseLTC =
      entity?.leverage?.adjustedPurchaseLTC ?? this.adjustedPurchaseLTC;
    this.adjustedARV = entity?.leverage?.adjustedARV ?? this.adjustedARV;
    this.submittedDate = entity?.dealTerms?.submittedDate ?? this.submittedDate;
    this.highRisk = entity?.dealTerms?.highRisk ?? this.highRisk;
    this.dealBudgetPricing =
      entity?.dealTerms?.dealBudgetPricing ?? this.dealBudgetPricing;
    this.deferredOrigPercent = entity?.loanTerm.deferredOrigPercent ?? 0;
    this.iREscrowOverrideInt = entity?.loanCostOverride?.iREscrowOverrideInt;
    this.iREscrowOverrideMoneyAmt =
      entity?.loanCostOverride?.iREscrowOverrideMoneyAmt;
    this.processingFeeOverride =
      entity?.exceptionPricingOverride?.processingFeeOverride;
    this.completedExits = entity?.borrowerInfo?.completedExits;
    this.wholesalePartner = entity?.borrowerInfo?.wholesalePartner ?? "";
    this.maxInterestReserve = entity?.loanCosts?.maxInterestReserve;
    this.documentPrepFee = entity?.loanTerm?.documentPrepFee ?? "";
  }
}

const baseYupObject = {
  afterCompletedVal: yup
    .number()
    .min(1, "Value must be greater than 0")
    .when("loanProduct", {
      is: "BridgePlus",
      then: (schema) => schema.min(0),
      otherwise: (schema) => schema,
    })
    .required("Value After Completion is required"),
  appraisalFee: yup.number().required("Valuation Fee is required"),
  borrowingEntity: yup.string().required("Borrowing Entity is required"),
  borrowerIsReferral: yup.string(),
  brokerFee: yup
    .number()
    .min(0, "Negative values are not permitted for this selection")
    .required("Broker Fee is required"),
  brokerProcessingFee: yup
    .number()
    .required("Broker Processing Fee is required"),
  buildsCompletedInThreeYears: yup
    .number()
    .required("Builds Completed & Sold is required"),
  cashOut: yup.string().required(),
  channel: yup.string().required("Loan Channel is required"),
  city: yup.string().required("City is required"),
  combinedLiquidity: yup.number().required("Combined Liquidity is required"),
  enabled: yup.boolean().required(),
  existingDebtStatus: yup.string().required("Existing Debt Status is required"),
  fnfCompletedInThreeYears: yup
    .number()
    .required("Fix & Flips Completed & Sold is required"),
  guaranty: yup.string().required("Guaranty is required"),
  heavyRehab: yup.string().required(),
  lesserOfLotOrPurchase: yup
    .number()
    .min(1, "Value must be greater than 0")
    .required("Lot Value or Purchase Price is required"),
  loanProduct: yup.string().required("Loan Product is required"),
  loanPurpose: yup.string().required("Loan Purpose is required"),
  loanTerm: yup.string().required("Loan Term is required"),
  overrideMaster: yup.string().required(),
  primaryGuarantor: yup.string().required("Primary Guarantor is required"),
  propertyAddress: yup.string().required("Street is required"),
  propertyType: yup.string().required("Property Type is required"),
  qualifyingFICO: yup
    .number()
    .required("Credit Score is required")
    .max(850, "Credit Score must be less than or equal to 850"),
  quoteName: yup.string().required("Quote Name is required"),
  quoteType: yup.string().required("Quote Type is required"),
  referralFee: yup.string().required(),
  internalRefinance: yup.string(),
  exitFeeCheck: yup.string(),
  refinance: yup.string().required(),
  rehabConBudget: yup
    .number()
    .required("Rehab/Construction Budget is required"),
  renovationCostsAfterPurchase: yup.number(),
  totalPurchasePrice: yup.number(),
  completedTransactions: yup
    .number()
    .required("Completed Transactions is required"),
  secondaryGuarantor: yup.string(),
  state: yup.string().required("State is required"),
  targetCloseDate: yup.string().required("Target Close Date is required"),
  tier: yup.string(),
  timeCloseToPayoff: yup.number().required("Months to Payoff is required"),
  usCitizen: yup.string().required(),
  zip: yup
    .string()
    .matches(/(^\d{5}$)|(^\d{5}-\d{4}$)/, "Invalid Zip Code")
    .required("Zip is required"),
  originalPurchaseDate: yup
    .string()
    .required("Original Purchase Date is required"),
  payoff: yup.number().required(),
  username: yup.string(),
  altTier: yup.string(),
  borrowerTier: yup.string().required("Borrower Tier is required"),
  buyUpPercentage: yup
    .number(),
  blendedLTCOverride: yup.number(),
  purchaseLTCOverride: yup.number(),
  arvOverride: yup.number(),
  commissionReduction: yup.number(),
  autoRate: yup.number(),
  autoFee: yup.number(),
  finalOrigFeeOverride: yup.number(),
  rateReductionOverride: yup.number(),
  finalRateOverride: yup.number(),
  isZeroOrigFee: yup.string(),
  exceptionType: yup.string(),
  numOfProperties: yup.number(),
  deferredOrigPercent: yup.number(),
  achExempt: yup.string(),
  achRequired: yup.string(),
  completedExits: yup.number(),
  wholesalePartner: yup.string(),
};

const externalYupObject = {
  ...baseYupObject,
  brokerFee: yup
    .number()
    .min(0, "Negative values are not permitted for this selection")
    .max(3, "Fee percentage must be less than 3")
    .required("Broker Fee is required"),
};

export const FixAndFlipFormSchema = yup.object(baseYupObject);

export const ExternalFixAndFlipFormSchema = yup.object(externalYupObject);
